import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  HiOutlineUpload,
  HiOutlineDocumentText,
  HiOutlineX,
  HiCheck,
  HiExclamation,
  HiLockClosed,
  HiShieldCheck,
  HiDocumentSearch,
  HiKey,
} from "react-icons/hi";
import { uploadAgentFiles, checkDisposableUuid } from "../../services/api";
import LoadingState from "../Common/LoadingState";
import { useTheme } from "../../contexts/ThemeContext";

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ACCEPTED_FILE_TYPES = [
  ".pdf",
  ".docx",
  ".txt",
  ".csv",
  ".xlsx",
  ".md",
  ".json",
];

const AgentFileUpload: React.FC = () => {
  const { agentUuid } = useParams<{ agentUuid: string }>();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [files, setFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isChecking, setIsChecking] = useState(true);
  const [isValidUuid, setIsValidUuid] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Logo to display based on current theme
  const logoSrc =
    theme === "light"
      ? "/assets/ukti-logo-dark-background.png"
      : "/assets/ukti-logo-with-text-white.png";

  const isFileTypeAccepted = (file: File) => {
    const fileExt = "." + file.name.split(".").pop()?.toLowerCase();
    return ACCEPTED_FILE_TYPES.includes(fileExt);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const fileArray = Array.from(e.target.files);
      validateAndAddFiles(fileArray);
    }
  };

  const validateAndAddFiles = (fileArray: File[]) => {
    setIsScanning(true);
    const validFiles: File[] = [];
    let errorMsg: string | null = null;

    fileArray.forEach((file) => {
      if (!isFileTypeAccepted(file)) {
        errorMsg = `File type not accepted. Allowed types: ${ACCEPTED_FILE_TYPES.join(
          ", "
        )}`;
      } else if (file.size > MAX_FILE_SIZE) {
        errorMsg = "File too large. Maximum size is 10MB.";
      } else {
        validFiles.push(file);
      }
    });

    // Simulate scanning for security
    setTimeout(() => {
      if (validFiles.length > 0) {
        setFiles((prevFiles) => [...prevFiles, ...validFiles]);
      }

      if (errorMsg) {
        setError(errorMsg);
        setTimeout(() => setError(null), 5000);
      }
      setIsScanning(false);
    }, 1200);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    if (e.dataTransfer.files.length) {
      validateAndAddFiles(Array.from(e.dataTransfer.files));
    }
  };

  const removeFile = (index: number) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (files.length === 0 || !agentUuid) {
      setError("No files selected or upload UUID is missing");
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      const result = await uploadAgentFiles(agentUuid, files);

      if (result.success) {
        setUploadSuccess(true);
        // Redirect to login page after successful upload
        setTimeout(() => {
          navigate("/login");
        }, 3000);
      } else {
        setError(result.message || "Upload failed");
      }
    } catch (err: any) {
      setError(err.message || "An unexpected error occurred");
    } finally {
      setIsUploading(false);
    }
  };

  // Check if the UUID is valid
  useEffect(() => {
    const validateUuid = async () => {
      if (!agentUuid) {
        setError("Missing upload UUID");
        setIsChecking(false);
        return;
      }

      try {
        setIsChecking(true);
        const result = await checkDisposableUuid(agentUuid);

        if (result.valid) {
          setIsValidUuid(true);
        } else {
          setError("This upload link is no longer valid");
          // Redirect to login after a delay
          setTimeout(() => {
            navigate("/login");
          }, 3000);
        }
      } catch (err: any) {
        setError("Failed to verify upload link");
      } finally {
        setIsChecking(false);
      }
    };

    validateUuid();
  }, [agentUuid, navigate]);

  // Return to login page if no agent ID is provided
  useEffect(() => {
    if (!agentUuid) {
      navigate("/login");
    }
  }, [agentUuid, navigate]);

  const getFileIcon = (fileName: string) => {
    const ext = fileName.split(".").pop()?.toLowerCase();
    switch (ext) {
      case "pdf":
        return "PDF";
      case "docx":
        return "DOC";
      case "txt":
        return "TXT";
      case "csv":
      case "xlsx":
        return "CSV";
      case "md":
        return "MD";
      case "json":
        return "JSON";
      default:
        return "FILE";
    }
  };

  // If still checking the UUID validity, show loading state
  if (isChecking) {
    return (
      <LoadingState message="Verifying secure upload link..." fullPage={true} />
    );
  }

  // If the UUID is invalid, show error state
  if (!isValidUuid && !isChecking) {
    return (
      <div className="min-h-screen bg-[var(--background)] flex flex-col items-center justify-center">
        <div className="absolute top-4 left-0 right-0 flex justify-center">
          <img src={logoSrc} alt="Ukti.ai" className="h-16 w-auto" />
        </div>

        <motion.div
          className="max-w-lg w-full px-6 py-8 bg-[var(--card)] rounded-xl shadow-lg text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[var(--destructive)]/20 flex items-center justify-center text-[var(--destructive)]">
            <HiExclamation className="w-8 h-8" />
          </div>
          <h2 className="text-2xl font-bold mb-3 text-[var(--foreground)]">
            Invalid Upload Link
          </h2>
          <p className="text-[var(--foreground-muted)] mb-6">
            {error ||
              "This upload link is no longer valid or has already been used."}
            <br />
            You will be redirected to the login page shortly.
          </p>
          <motion.button
            className="px-6 py-2.5 bg-[var(--accent)] text-white rounded-md font-medium"
            onClick={() => navigate("/login")}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Go to Login
          </motion.button>
        </motion.div>
      </div>
    );
  }

  if (!agentUuid) {
    return <LoadingState message="Redirecting to login..." fullPage={true} />;
  }

  if (uploadSuccess) {
    return (
      <div className="min-h-screen bg-[var(--background)] flex flex-col items-center justify-center">
        <div className="absolute top-4 left-0 right-0 flex justify-center">
          <img src={logoSrc} alt="Ukti.ai" className="h-16 w-auto" />
        </div>

        <motion.div
          className="max-w-lg w-full px-6 py-8 bg-[var(--card)] rounded-xl shadow-lg text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <motion.div
            className="w-16 h-16 mx-auto mb-4 rounded-full bg-[var(--accent)]/20 flex items-center justify-center text-[var(--accent)] border border-[var(--accent)]"
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", damping: 10, stiffness: 100 }}
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.3, type: "spring", damping: 8 }}
            >
              <HiCheck className="w-8 h-8" />
            </motion.div>
          </motion.div>
          <motion.h2
            className="text-2xl font-bold mb-3 text-[var(--foreground)]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            Upload Successful!
          </motion.h2>
          <motion.p
            className="text-[var(--foreground-muted)] mb-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            Your files have been securely uploaded and encrypted. You will be
            redirected to the login page shortly.
          </motion.p>
          <motion.button
            className="px-6 py-2.5 bg-[var(--accent)] text-white rounded-md font-medium"
            onClick={() => navigate("/login")}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            Go to Login
          </motion.button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--background)] flex flex-col relative">
      {/* Secure Connection Indicator */}
      <motion.div
        className="absolute top-2 right-2 sm:top-4 sm:right-4 flex items-center px-2 py-1 rounded-full bg-green-100 text-green-700 text-xs font-medium"
        initial={{ x: 20, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{
          delay: 0.5,
          duration: 0.5,
          type: "spring",
          damping: 12,
        }}
      >
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            repeatDelay: 5,
          }}
        >
          <HiLockClosed className="w-3 h-3 mr-1" />
        </motion.div>
        Secure Connection
      </motion.div>

      <div className="w-full py-6 flex justify-center">
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <img src={logoSrc} alt="Ukti.ai" className="h-16 sm:h-16 w-auto" />
        </motion.div>
      </div>

      <div className="flex-1 relative z-10">
        <div className="container-large py-6 md:py-8">
          <div className="text-center w-full px-4 pt-4">
            <motion.div
              className="rounded-xl p-6 md:p-8 mb-8 max-w-5xl mx-auto bg-[var(--card-container)] backdrop-blur-sm border border-[var(--border)]"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              <motion.div
                className="flex items-center justify-center mb-2"
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <motion.div
                  animate={{
                    rotateZ: [0, 10, -10, 0],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    repeatDelay: 7,
                  }}
                >
                  <HiShieldCheck className="w-6 h-6 text-green-500 mr-2" />
                </motion.div>
                <h2 className="text-xl md:text-2xl font-bold text-[var(--foreground)] text-center">
                  Secure File Upload
                </h2>
              </motion.div>

              <motion.p
                className="text-[var(--foreground-muted)] mb-4 text-center max-w-lg mx-auto"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                Upload files securely to enhance the agent's knowledge base.
                Your data is encrypted and protected.
              </motion.p>

              <motion.div
                className="bg-blue-50 text-blue-800 p-3 rounded-lg mb-6 text-sm"
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <p className="font-medium mb-1">Accepted file types:</p>
                <p>
                  {ACCEPTED_FILE_TYPES.join(", ")} (Maximum file size: 10MB)
                </p>
              </motion.div>

              {/* File Upload Area */}
              <motion.div
                className={`border-2 border-dashed rounded-lg p-8 mb-6 text-center transition-colors ${
                  isDragging
                    ? "border-[var(--accent)] bg-[var(--accent)]/10"
                    : "border-[var(--border)] hover:border-[var(--accent)]/50"
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                whileHover={{
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                  y: -2,
                }}
              >
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  multiple
                  accept={ACCEPTED_FILE_TYPES.join(",")}
                  className="hidden"
                />
                <motion.div
                  className="w-16 h-16 mx-auto mb-4 rounded-full bg-[var(--accent)]/20 flex items-center justify-center text-[var(--accent)]"
                  whileHover={{ scale: 1.05 }}
                  animate={
                    isDragging
                      ? {
                          y: [0, -5, 0],
                          backgroundColor: [
                            "rgba(var(--accent-rgb), 0.2)",
                            "rgba(var(--accent-rgb), 0.3)",
                            "rgba(var(--accent-rgb), 0.2)",
                          ],
                        }
                      : {}
                  }
                  transition={
                    isDragging ? { repeat: Infinity, duration: 1.5 } : {}
                  }
                >
                  <motion.div
                    animate={!isDragging ? { y: [0, -3, 0] } : {}}
                    transition={{
                      repeat: Infinity,
                      repeatDelay: 3,
                      duration: 1,
                    }}
                  >
                    <HiOutlineUpload className="w-8 h-8" />
                  </motion.div>
                </motion.div>
                <p className="text-[var(--foreground)] font-medium mb-2">
                  Drag and drop files here, or click to browse
                </p>
                <p className="text-[var(--foreground-muted)] text-sm">
                  Files will be automatically scanned for safety
                </p>
              </motion.div>

              {/* File List */}
              {files.length > 0 && (
                <motion.div
                  className="mb-6"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  transition={{ duration: 0.3 }}
                >
                  <h3 className="text-lg font-medium mb-3 text-[var(--foreground)]">
                    Selected Files ({files.length})
                  </h3>
                  <div className="space-y-2 max-h-60 overflow-y-auto pr-2">
                    <AnimatePresence>
                      {files.map((file, index) => (
                        <motion.div
                          key={`${file.name}-${index}`}
                          className="flex items-center justify-between p-3 bg-[var(--card)] rounded-lg"
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, x: -10 }}
                          transition={{ duration: 0.2 }}
                          whileHover={{
                            backgroundColor: "var(--card-hover)",
                            x: 2,
                          }}
                        >
                          <div className="flex items-center">
                            <motion.div
                              className="w-8 h-8 rounded bg-[var(--accent)]/10 text-[var(--accent)] flex items-center justify-center text-xs font-medium mr-3"
                              initial={{ scale: 0.8, opacity: 0.5 }}
                              animate={{ scale: 1, opacity: 1 }}
                              transition={{ delay: 0.1 }}
                            >
                              {getFileIcon(file.name)}
                            </motion.div>
                            <div className="overflow-hidden">
                              <p className="text-[var(--foreground)] text-sm font-medium truncate max-w-[300px]">
                                {file.name}
                              </p>
                              <p className="text-[var(--foreground-muted)] text-xs">
                                {(file.size / 1024).toFixed(1)} KB{" "}
                                <motion.span
                                  className="text-green-600 ml-1"
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  transition={{ delay: 0.3 }}
                                >
                                  ✓ Verified
                                </motion.span>
                              </p>
                            </div>
                          </div>
                          <motion.button
                            onClick={(e) => {
                              e.stopPropagation();
                              removeFile(index);
                            }}
                            className="p-1.5 text-[var(--foreground-muted)] hover:text-[var(--destructive)] hover:bg-[var(--muted)] rounded-full"
                            aria-label="Remove file"
                            whileHover={{
                              scale: 1.1,
                              backgroundColor: "var(--destructive-light)",
                            }}
                            whileTap={{ scale: 0.95 }}
                          >
                            <HiOutlineX className="w-4 h-4" />
                          </motion.button>
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </div>
                </motion.div>
              )}

              {/* Error Message */}
              <AnimatePresence>
                {error && (
                  <motion.div
                    className="mb-6 p-4 bg-[var(--destructive)]/10 border border-[var(--destructive)]/30 rounded-lg text-[var(--destructive)] flex items-center"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                  >
                    <motion.div
                      animate={{
                        rotate: [0, 5, -5, 0],
                      }}
                      transition={{
                        duration: 0.5,
                        repeat: 2,
                      }}
                    >
                      <HiExclamation className="w-5 h-5 mr-2 flex-shrink-0" />
                    </motion.div>
                    <span>{error}</span>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Upload Button */}
              <div className="flex justify-center">
                <motion.button
                  className={`px-6 py-3 rounded-lg font-medium text-white ${
                    files.length === 0 || isUploading
                      ? "bg-[var(--accent)]/50 cursor-not-allowed"
                      : "bg-[var(--accent)] hover:bg-[var(--accent-hover)]"
                  } transition-colors`}
                  onClick={handleUpload}
                  disabled={files.length === 0 || isUploading}
                  whileHover={
                    files.length > 0 && !isUploading
                      ? {
                          scale: 1.05,
                          boxShadow: "0 4px 10px rgba(0, 0, 0, 0.2)",
                        }
                      : {}
                  }
                  whileTap={
                    files.length > 0 && !isUploading ? { scale: 0.95 } : {}
                  }
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                >
                  {isUploading ? (
                    <>
                      <span className="inline-block w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin mr-2"></span>
                      Encrypting & Uploading...
                    </>
                  ) : (
                    <>
                      <motion.div
                        className="inline-block mr-2"
                        animate={{
                          y: [0, -2, 0],
                        }}
                        transition={{
                          duration: 1.5,
                          repeat: Infinity,
                          repeatDelay: 2,
                        }}
                      >
                        <HiLockClosed className="w-4 h-4" />
                      </motion.div>
                      Upload Securely
                    </>
                  )}
                </motion.button>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentFileUpload;
